import { createLiveQuery, type LiveQuery, type LiveQuerySnapshot } from "@hq/client";
import type { LocalStore, TableName } from "@hq/local-store";
import { useState, useSyncExternalStore } from "react";
import { useStore } from "./provider";

export type LiveQueryResult<T> = {
  data: T | undefined;
  /** True while the query for the current deps has not resolved yet. `data` may still hold the previous deps' result. */
  isLoading: boolean;
};

type Params<T> = { tables: readonly TableName[]; run: (store: LocalStore) => Promise<T> };

/**
 * Owns one LiveQuery per hook instance. The query is created when React subscribes (after
 * commit) and disposed when the last subscriber leaves, so Strict Mode's mount/unmount/mount
 * never leaks a query. Keyed by the deps key: a new key means a new query, and the previous
 * data is kept until the new one resolves so lists never flash empty.
 */
class LiveQueryController<T> {
  private query: LiveQuery<T> | undefined;
  private queryKey: string | undefined;
  private unsubscribeQuery: (() => void) | undefined;
  private readonly listeners = new Set<() => void>();
  private subscribeKey: string | undefined;
  private subscribeFn: ((onChange: () => void) => () => void) | undefined;
  private lastSnapshot: LiveQuerySnapshot<T> | undefined;
  private lastData: T | undefined;
  private loaded: LiveQueryResult<T> | undefined;
  private loading: LiveQueryResult<T> = { data: undefined, isLoading: true };

  constructor(private readonly store: LocalStore) {}

  /** Stable per key, so React resubscribes exactly when the deps change. */
  subscribeFor(key: string, params: Params<T>): (onChange: () => void) => () => void {
    if (this.subscribeKey !== key || !this.subscribeFn) {
      this.subscribeKey = key;
      this.subscribeFn = (onChange) => this.attach(key, params, onChange);
    }
    return this.subscribeFn;
  }

  snapshotFor(key: string): LiveQueryResult<T> {
    const snapshot = this.queryKey === key ? this.query?.snapshot() : undefined;
    if (snapshot && !snapshot.isLoading) {
      if (this.lastSnapshot !== snapshot || !this.loaded) {
        this.lastSnapshot = snapshot;
        this.lastData = snapshot.data;
        this.loaded = { data: snapshot.data, isLoading: false };
      }
      return this.loaded;
    }
    if (this.loading.data !== this.lastData) {
      this.loading = { data: this.lastData, isLoading: true };
    }
    return this.loading;
  }

  private attach(key: string, params: Params<T>, onChange: () => void): () => void {
    this.listeners.add(onChange);
    this.ensure(key, params);
    return () => {
      this.listeners.delete(onChange);
      if (this.listeners.size === 0) this.teardown();
    };
  }

  private ensure(key: string, params: Params<T>): void {
    if (this.query && this.queryKey === key) return;
    this.teardown();
    const query = createLiveQuery(this.store, params.tables, params.run);
    this.query = query;
    this.queryKey = key;
    this.unsubscribeQuery = query.subscribe(() => {
      for (const l of [...this.listeners]) l();
    });
  }

  private teardown(): void {
    this.unsubscribeQuery?.();
    this.query?.dispose();
    this.unsubscribeQuery = undefined;
    this.query = undefined;
    this.queryKey = undefined;
  }
}

function depsKey(deps: readonly unknown[]): string {
  return JSON.stringify(deps, (_k, v: unknown) => (v === undefined ? null : v));
}

/**
 * Subscribe a component to a store query. Re-runs when one of `tables` commits and when
 * `deps` change (compared by value). No effects: the subscription is `useSyncExternalStore`'s.
 */
export function useLiveQuery<T>(
  tables: readonly TableName[],
  run: (store: LocalStore) => Promise<T>,
  deps: readonly unknown[],
): LiveQueryResult<T> {
  const store = useStore();
  const [controller] = useState(() => new LiveQueryController<T>(store));
  const key = depsKey(deps);
  const subscribe = controller.subscribeFor(key, { tables, run });
  return useSyncExternalStore(
    subscribe,
    () => controller.snapshotFor(key),
    () => controller.snapshotFor(key),
  );
}
